import { useLocation } from 'react-router-dom'
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import './fund-section.css'
import { Notices } from './fundSectionNotices'

export default function FundSection({
  title,
  children,
  actions,
  attention,
  collapsible = true,
  defaultOpen = false,
  revealKey,
  className = '',
  testId,
}: {
  title: string
  children: ReactNode
  actions?: ReactNode
  attention?: string | null
  collapsible?: boolean
  defaultOpen?: boolean
  revealKey?: string | null
  className?: string
  testId?: string
}) {
  const location = useLocation()
  const anchor = title.toLowerCase().replace(/ /g, '-')
  const linked = location.hash === `#${anchor}`
  const [open, setOpen] = useState(defaultOpen || linked)
  const [notices, setNotices] = useState<Record<string, string>>({})
  const id = useId()
  const report = useCallback((key: string, text: string | null) => {
    setNotices((previous) => {
      if ((previous[key] ?? null) === text) return previous
      const next = { ...previous }
      if (text) next[key] = text
      else delete next[key]
      return next
    })
  }, [])
  useEffect(() => {
    if (revealKey || linked) setOpen(true)
  }, [revealKey, linked, location.key])
  const notice = useMemo(
    () => [...new Set([attention, ...Object.values(notices)].filter(Boolean))].join(' · '),
    [attention, notices]
  )
  const expanded = !collapsible || open
  return (
    <section
      className={`fa-card ${collapsible ? 'fund-section' : ''} ${className}`}
      id={anchor}
      data-testid={testId}
    >
      {collapsible ? (
        <div className="fund-section__header">
          <h2>
            <button
              type="button"
              aria-label={title}
              aria-expanded={expanded}
              aria-controls={id}
              onClick={() => setOpen((value) => !value)}
            >
              <span>{title}</span>
              {!expanded && notice ? <small role="status">{notice}</small> : null}
              <span className="fund-section__chevron" aria-hidden="true">
                {expanded ? '⌄' : '›'}
              </span>
            </button>
          </h2>
          {expanded && actions ? <div className="fund-section__actions">{actions}</div> : null}
        </div>
      ) : (
        <div className="fa-chd">
          {title}
          {actions}
        </div>
      )}
      <Notices.Provider value={report}>
        <div id={id} hidden={!expanded} className={collapsible ? 'fund-section__body' : undefined}>
          {children}
        </div>
      </Notices.Provider>
    </section>
  )
}
